import { BehaviorSubject, fromEvent, merge, Subject, of } from 'rxjs';
import { auditTime, delay, map, switchMap, takeUntil } from 'rxjs/operators';
import {
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { PhxTooltipComponent, PhxTooltipIntent, PhxTooltipSize } from './phx-tooltip.component';
import { POSITION_TYPE } from '../dropdown/overlay-position';
import { PhxTooltipFixedWidth, InteractionType } from './phx-tooltip.types';

export type PhxTriggerType = 'hover' | 'click' | 'always' | 'none';

export interface PhxToggleTipConfig {
  intersectionRootMargin?: string;
  intersectionThreshold?: number;
  actionsMaxHeight?: number;
};

@Directive({
  selector: '[phx-tooltip]',
  exportAs: 'phxTooltip',
})
export class PhxTooltipDirective implements OnChanges, OnDestroy {
  @Input() phxTitle!: string | TemplateRef<any> | null;
  @Input() phxSubtitle!: string | TemplateRef<any> | null;
  @Input() phxContent!: string | TemplateRef<any> | null;
  @Input() phxTrigger: PhxTriggerType = 'hover';
  @Input() phxPlacement: POSITION_TYPE = 'top';
  @Input() phxTooltipSize: PhxTooltipSize = 'medium';
  @Input() phxTooltipMaxWidth!: number;
  @Input() phxTooltipFixedWidth!: PhxTooltipFixedWidth;
  @Input() phxIntent: PhxTooltipIntent = 'neutral';
  @Input() phxTooltipContext: any;
  @Input() phxTooltipOverlayClassName!: string;
  @Input() phxShowBackdrop: boolean = false;
  @Input() phxShowCloseIcon: boolean = false;
  @Input() phxToggleTipContent!: string | TemplateRef<any> | null;
  @Input() phxIsToggleTip: boolean = false;
  @Input() phxPaddingNone: boolean = false;
  @Input()
  set phxToggleTipConfig(value: PhxToggleTipConfig) {
    this._phxToggleTipConfig = { ...this._phxToggleTipConfig, ...value };
  }

  @Output() phxTooltipVisibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() phxTooltipCloseIconClicked: EventEmitter<void> = new EventEmitter<void>();

  private componentFactory: ComponentFactory<PhxTooltipComponent> = this.resolver.resolveComponentFactory(PhxTooltipComponent);
  private tooltip: PhxTooltipComponent;
  private componentRef: ComponentRef<PhxTooltipComponent>;
  private mouseEnterDelay = 150;
  private mouseLeaveDelay = 100;
  private phxTrigger$: BehaviorSubject<PhxTriggerType> = new BehaviorSubject<PhxTriggerType>('hover');
  private destroy$ = new Subject<void>();
  private isToggleTipVisible: boolean = false;
  private readonly SMALL_ELEMENT_THRESHOLD = 20;
  private lastInteractionType: InteractionType = 'hover';
  private readonly CORNER_PLACEMENTS = ['topRight', 'topLeft', 'bottomRight', 'bottomLeft'];
  private _intersectionObserver: IntersectionObserver | null = null;
  private _phxToggleTipConfig: PhxToggleTipConfig = {
    intersectionRootMargin: '0px 0px 0px 0px',
    intersectionThreshold: 1.0,
  };

  public showTooltip(): void {
    this.adjustOffsetsBeforeShow();
    
    if (this.phxIsToggleTip) {
      if (this.lastInteractionType === 'hover') {
        this.updateComponentValue('content', this.phxContent);
      } else if (this.lastInteractionType === 'click') {
        this.setupTriggerVisibilityObserver();
        this.updateComponentValue('content', this.phxToggleTipContent);
        this.updateComponentValue('isToggleTipVisible', true);
        this.isToggleTipVisible = true;
        this.tooltip.updateToggleTooltipPosition();
      }
    }
    this.tooltip.show();
  }

  public hideTooltip(): void {
    this.tooltip.hide();
    this.cleanupIntersectionObserver();
  }

  public hideToggleTip(): void {
    this.hideTooltip();
    this.updateComponentValue('content', this.phxContent);
    this.updateComponentValue('isToggleTipVisible', false);
    this.isToggleTipVisible = false;
    this.lastInteractionType = 'hover';
    this.cleanupIntersectionObserver();
  }

  public updateTooltip(): void {
    this.tooltip.updateTooltip();
  }

  public updateToggleTooltipPosition(): void {
    this.tooltip.updateToggleTooltipPosition();
  }

  private createTooltipComponent(): void {
    this.componentRef = this.viewContainerRef.createComponent(this.componentFactory);
    this.tooltip = this.componentRef.instance;
    this.renderer.removeChild(this.renderer.parentNode(this.elementRef.nativeElement), this.componentRef.location.nativeElement);
    this.tooltip.setOverlayOrigin(this.elementRef);
    this.updateTooltipProperties();
  }

  private updateTooltipProperties() {
    this.updateComponentValue('title', this.phxTitle);
    this.updateComponentValue('subtitle', this.phxSubtitle);
    this.updateComponentValue('content', this.phxContent);
    this.updateComponentValue('placement', this.phxPlacement);
    this.updateComponentValue('trigger', this.phxTrigger);
    this.updateComponentValue('overlayClassName', this.phxTooltipOverlayClassName);
    this.updateComponentValue('size', this.phxTooltipSize);
    this.updateComponentValue('intent', this.phxIntent);
    this.updateComponentValue('showBackdrop', this.phxShowBackdrop);
    this.updateComponentValue('phxTooltipVisibleChange', this.phxTooltipVisibleChange);
    this.updateComponentValue('showCloseIcon', this.phxShowCloseIcon);
    this.updateComponentValue('closeIconClicked', this.phxTooltipCloseIconClicked);
    this.updateComponentValue('fixedWidth', this.phxTooltipFixedWidth);
    this.updateComponentValue('maxWidth', this.phxTooltipMaxWidth);
    this.updateComponentValue('actionsMaxHeight', this._phxToggleTipConfig.actionsMaxHeight);
    this.updateComponentValue('tooltipContext', this.phxTooltipContext);
    this.updateComponentValue('paddingNone', this.phxPaddingNone);
    this.updateComponentValue('isToggleTip', this.phxIsToggleTip);
    this.tooltip.updateTooltip();
  }

  private updateChangedProperties(simpleChanges: SimpleChanges): void {
    const {
      phxTitle,
      phxTrigger,
      phxPlacement,
      phxSubtitle,
      phxContent,
      phxTooltipSize,
      phxIntent,
      phxShowBackdrop,
      phxShowCloseIcon,
      phxToggleTipConfig,
      phxTooltipOverlayClassName,
      phxTooltipFixedWidth,
      phxTooltipMaxWidth,
      phxTooltipContext,
      phxPaddingNone,
    } = simpleChanges;

    if (phxTitle) this.updateComponentValue('title', this.phxTitle);
    if (phxTrigger) this.updateComponentValue('trigger', this.phxTrigger);
    if (phxPlacement) this.updateComponentValue('placement', this.phxPlacement);
    if (phxSubtitle) this.updateComponentValue('subtitle', this.phxSubtitle);
    if (phxContent) this.updateComponentValue('content', this.phxContent);
    if (phxTooltipSize) this.updateComponentValue('size', this.phxTooltipSize);
    if (phxIntent) this.updateComponentValue('intent', this.phxIntent);
    if (phxShowBackdrop) this.updateComponentValue('showBackdrop', this.phxShowBackdrop);
    if (phxShowCloseIcon) this.updateComponentValue('showCloseIcon', this.phxShowCloseIcon);
    if (phxToggleTipConfig) this.updateComponentValue('actionsMaxHeight', this._phxToggleTipConfig.actionsMaxHeight);
    if (phxTooltipOverlayClassName) this.updateComponentValue('overlayClassName', this.phxTooltipOverlayClassName);
    if (phxTooltipFixedWidth) this.updateComponentValue('fixedWidth', this.phxTooltipFixedWidth);
    if (phxTooltipMaxWidth) this.updateComponentValue('maxWidth', this.phxTooltipMaxWidth);
    if (phxTooltipContext) this.updateComponentValue('tooltipContext', this.phxTooltipContext);
    if (phxPaddingNone) this.updateComponentValue('paddingNone', this.phxPaddingNone);
    this.tooltip.updateTooltip();
  }

  private updateComponentValue(key: string, value: any): void {
    if (typeof value !== 'undefined') this.tooltip[key] = value;
  }

  private attachListeners(): void {
    const mouseEnter$ = fromEvent(this.nativeElement, 'mouseenter').pipe(
      map(() => {
        this.lastInteractionType = this.lastInteractionType === 'click' ? 'click' : 'hover';
        return true;
      })
    );

    const mouseLeave$ = fromEvent(this.nativeElement, 'mouseleave').pipe(map(() => false));

    const hoverStates$ = merge(mouseEnter$, mouseLeave$, this.tooltip.mouseState$).pipe(
      switchMap((visible) => {
        if (visible) {
          return of(true).pipe(delay(this.mouseEnterDelay));
        } else {
          return of(false).pipe(delay(this.mouseLeaveDelay));
        }
      })
    );

    const clickStates$ = fromEvent(this.nativeElement, 'click').pipe(
      map(() => {
        this.lastInteractionType = 'click';
        return true;
      })
    );

    const clickOrHoverState$ = this.phxTrigger$.pipe(
      switchMap((trigger) => {
        if (trigger === 'click') return clickStates$;
        else if (trigger === 'hover' && this.phxIsToggleTip) {
          // Special case: Toggle-tip need to respond to both hover AND click events with the trigger set to hover
          // - Hover shows the regular tooltip content (phxContent)
          // - Click shows the toggle-tip content (phxToggleTipContent) and keeps it visible
          return merge(hoverStates$, clickStates$);
        }
        else if (trigger === 'hover') return hoverStates$;
        else if (trigger === 'always') return of(true);
        else if (trigger === 'none') return of(false);
        else return of(false);
      }),
      auditTime(100),
      takeUntil(this.destroy$)
    );

    clickOrHoverState$.subscribe((visible) => {
      if (visible) {
        this.showTooltip();
      } else {
        // If toggleTip is visible and last interaction was click, do not hide on mouseleave
        if (this.phxIsToggleTip && this.isToggleTipVisible && this.lastInteractionType === 'click') {
          return;
        }
        this.hideTooltip();
        this.isToggleTipVisible = false;
      }
    });

    // Handle the click outside the toggle tip to hide it.
    this.tooltip.outsideClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: MouseEvent) => {
        this.handleOutsideClick(event);
      });
  }

  private calculateAutomaticOffset(elementWidth: number, placement: string): { x: number; y: number } {
    // Only apply offsets for small elements in corner placements
    if (elementWidth > this.SMALL_ELEMENT_THRESHOLD || !this.CORNER_PLACEMENTS.includes(placement)) {
      return { x: 0, y: 0 };
    }

    const offset = elementWidth / 2;
    const isRightPlacement = placement.endsWith('Right');
    const isLeftPlacement = placement.endsWith('Left');

    return {
      x: isRightPlacement ? offset : isLeftPlacement ? -offset : 0,
      y: 0,
    };
  }

  private adjustOffsetsBeforeShow(): void {
    const elementWidth = this.nativeElement.offsetWidth;

    // Calculate and apply automatic offsets
    const { x, y } = this.calculateAutomaticOffset(elementWidth, this.phxPlacement);

    // Apply calculated offsets
    this.updateComponentValue('offsetX', x);
    this.updateComponentValue('offsetY', y);
  }

  private get nativeElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  private handleOutsideClick: (event: MouseEvent) => void = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.hideToggleTip();
  };

  private setupTriggerVisibilityObserver(): void {
    this.cleanupIntersectionObserver();
    if (!this.elementRef?.nativeElement || !this.phxIsToggleTip) {
      return;
    }

    const scrollableContainer = this.findClosestScrollableContainer();
    const options: IntersectionObserverInit = {
      root: scrollableContainer, // Use scrollable container as root instead of viewport, this is done to make the rootMargin work effectively
      rootMargin: this._phxToggleTipConfig.intersectionRootMargin,
      threshold: this._phxToggleTipConfig.intersectionThreshold,
    };
    this._intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          this.hideToggleTip();
        }
      });
    }, options);

    this._intersectionObserver.observe(this.elementRef.nativeElement);
  }

  private cleanupIntersectionObserver(): void {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
      this._intersectionObserver = null;
    }
  }

  private findClosestScrollableContainer(): HTMLElement | null {
    try {
      // Get all scrollable containers that are ancestors of this element
      const scrollableContainers = this.scrollDispatcher.getAncestorScrollContainers(this.elementRef);

      if (scrollableContainers.length > 0) {
        // Return the closest scrollable container (first in the array)
        const closestScrollable = scrollableContainers[0].getElementRef().nativeElement as HTMLElement;
        return closestScrollable;
      }
      return null; // Use viewport as root
    } catch (error) {
      return null; // Fallback to viewport
    }
  }

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    private renderer: Renderer2,
    private scrollDispatcher: ScrollDispatcher
  ) { }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    const { phxTrigger } = simpleChanges;
    if (phxTrigger) {
      this.phxTrigger$.next(phxTrigger.currentValue);
    }
    if (this.tooltip) {
      this.updateChangedProperties(simpleChanges);
    }
  }

  public ngOnInit(): void {
    this.createTooltipComponent();
    this.attachListeners();
  }

  public ngOnDestroy(): void {
    this.cleanupIntersectionObserver();
    this.destroy$.next();
    this.destroy$.complete();
    this.phxTrigger$.complete();
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }
}
